// The line's settings, behind the gear on its info card.
//
// WHY A MODAL. The page had these as an always-open form with its own Save, which put two
// save-shaped decisions on one screen: "Save" (Verso) and "Save and Apply" (Verso + push).
// An operator reading top to bottom could not tell which one they needed. So the page is
// now information plus one verb — Apply — and editing is a deliberate detour: gear, change,
// Save, back to the page (Kevin, 2026-08-04).
//
// SAVE HERE IS VERSO-ONLY, WITH ONE EXCEPTION. Nothing in this modal touches the PBX or GDMS
// — recording what a line IS should never be the thing that reconfigures a phone system. What
// lands where is then visible as pending work on the page, and Apply is what reaches the PBX.
//
// The exception is the CALLER ID, which is a carrier record and has no build step to wait for:
// `PUT /dids/:e164/cnam` submits it to Commio as part of Save. It is here because Kevin,
// 2026-08-19: "there are three separate gears for configuring the line. there should just be
// one in the top right of the card. this already has the 911 address setting. it just needs
// the separate field for the caller id that is different from line name." Three gears on one
// card — the card's, the caller ID's, the 911 address's — is three doors into the same room,
// and an operator cannot tell from looking which fields are behind which. So the sheet holds
// every field the card shows, and the copy says which one leaves the building.
//
// NAME AND CALLER ID ARE DIFFERENT THINGS and the sheet now shows that by holding both. The
// line's name is what Verso and the phone call it — an internal label on the phone screen and
// to other extensions. The caller ID is what strangers see when it calls them. They are
// usually similar, which is exactly why one field standing for both goes unnoticed until a
// customer's outbound calls show the wrong name.
//
// Three tables, one gesture. A line's settings live in `lines` (label, package, address),
// `lines.status`, `sip_servers` via the host write, and the extension's settings blob
// (voicemail). An operator does not care which; they get one Save. Sequential rather than
// parallel so the first refusal — the package/address type agreement — stops the rest
// instead of half-applying.
//
// VOICEMAIL ON/OFF IS DELIBERATELY ABSENT. FreePBX's disableVoiceMail can take the mailbox
// and its stored messages with it, so it is not reachable from a settings form (Kevin,
// 2026-08-04). The PIN reset is here because it is reversible.

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { usePbxNames } from '../../hooks/usePbxNames.js';
import { CNAM_MAX, cnamShapeHint, cnamState, seedResidentialCnam } from '../../lib/cnam.js';

// There WAS an `only` prop, scoping the sheet to one field so a card with its own gear opened
// just that field. It had one caller — the 911 address gear — and it existed because the card
// had grown a gear per field. One gear needs no scoping (Kevin, 2026-08-19), so it is gone
// along with the two extra gears.
export default function LineSettingsModal({ line, onClose, onSaved }) {
  const [form, setForm] = useState({
    label: line.label || '',
    packageId: line.packageId ? String(line.packageId) : '',
    locationId: line.locationId ? String(line.locationId) : '',
    status: line.status || 'active',
    pbxAgentId: line.pbxAgentId || '',
    resetPin: false,
  });
  const [error, setError] = useState(null);

  // The caller ID, in the shape the carrier holds it. `inherit` is the way back off an
  // override: without it a number given its own name can never follow the customer's again.
  // SEEDED WITH A NAME THAT FITS. Kevin, 2026-08-20: "fix caller id to be first initial, last name
  // in the cases where it exceeds the 15 character limit." Conrad Carpenter was stored as
  // `Conrad Carpent` and Cindy Mahler-Mor as `Cindy Mahler-Mo` — cut mid-surname, which is the half
  // that identifies somebody. Seeded from the SUBSCRIBER's full name rather than the stored value,
  // so opening this sheet on a truncated one proposes the fix instead of re-offering the truncation.
  const cnamSeed = seedResidentialCnam(line.cnam, line.subscriberName);
  const [cnam, setCnam] = useState({
    typed: line.cnam || '',
    first: cnamSeed.first,
    last: cnamSeed.last,
    inherit: false,
  });


  const { byId: pbxById } = usePbxNames();

  const tiersQ = useQuery({
    queryKey: ['service-tiers'],
    queryFn: () => api.get('/service-tiers'),
  });
  // Only this customer's addresses. The server refuses anyone else's, and a dropdown that
  // offers the impossible is a worse way to learn that than not offering it.
  const locsQ = useQuery({
    queryKey: ['subscriber', String(line.subscriberId), 'locations-for-line'],
    queryFn: () => api.get(`/subscribers/${line.subscriberId}`),
    enabled: Boolean(line.subscriberId),
  });

  const hostChanged = !line.provisioned && form.pbxAgentId !== (line.pbxAgentId || '');
  const statusChanged = form.status !== (line.status || 'active');
  // A BLANK LOCATION IS NOT A CHANGE. `updateLine` skips a null `locationId` (it tests
  // `!== undefined && !== null`), so the API cannot clear a line's 911 address — and this
  // used to count blank as dirty, arm Save, and then omit `location_id` from the body. The
  // screen read "clear the 911 address", the save reported success, and the address was
  // untouched. Blank now means "not choosing", which is what it can actually do.
  const locationChanged = Boolean(form.locationId)
    && form.locationId !== (line.locationId ? String(line.locationId) : '');

  // THE CALLER ID. Which shape the carrier wants comes from the number's own type, not from
  // this form: the caller-ID record has to match the 911 site type, so it is not a choice
  // made here. `cnamEditable` is the same guard the caller-ID card uses — the type comes from
  // the customer, so there is nothing to set before the number has one.
  const cnamType = line.locationType || line.packageType || line.subscriberType || null;
  const cnamEditable = Boolean(line.number && line.subscriberId);
  const cnamOwn = line.commioCallerIdId != null;
  const cnamCalc = cnamState(cnamType, cnam);
  // Incomplete is NOT dirty. A residential name with the surname deleted is half-typed, and
  // arming Save on it means submitting a record the carrier refuses.
  const cnamChanged = cnamEditable && (
    cnam.inherit
      ? cnamOwn
      : cnamCalc.complete && !cnamCalc.tooLong && cnamCalc.combined !== (line.cnam || '')
  );

  const dirty = form.label !== (line.label || '')
    || form.packageId !== (line.packageId ? String(line.packageId) : '')
    || locationChanged
    || statusChanged
    || hostChanged
    || cnamChanged
    || form.resetPin;

  const save = useMutation({
    mutationFn: async () => {
      const updated = await api.patch(`/lines/${line.id}`, {
        label: form.label,
        ...(form.packageId ? { package_id: Number(form.packageId) } : {}),
        // Only when it CHANGED. Re-sending the address the line already holds means an
        // unrelated edit — a label, a package — is refused with `location_not_theirs` when
        // that address is no longer one of the customer's, which is a refusal about a field
        // the operator never touched.
        ...(locationChanged ? { location_id: Number(form.locationId) } : {}),
      });
      if (hostChanged) await api.put(`/lines/${line.id}/pbx-host`, { pbx_agent_id: form.pbxAgentId || null });
      if (statusChanged) await api.patch(`/lines/${line.id}/status`, { status: form.status });
      if (form.resetPin && line.sipAccountId) {
        // Never `enabled` — see the header. Only the PIN.
        await api.put(`/sip-accounts/${line.sipAccountId}/voicemail-settings`, { reset_pin: true });
      }
      // LAST, because it is the only write here that leaves the building. A carrier refusal —
      // a name Commio will not take, a profile it cannot create — must not cost the operator
      // the label and package changes they made in the same sheet, and the error below names
      // the caller ID so it is clear what did and did not land.
      if (cnamChanged) {
        try {
          const cn = await api.put(
            `/dids/${encodeURIComponent(line.number)}/cnam`,
            cnam.inherit ? { inherit: true } : cnamCalc.payload,
          );
          if (updated && cn && typeof cn === 'object' && 'cnam' in cn) updated.cnam = cn.cnam;
        } catch (e) {
          const detail = e.body?.error || e.message;
          throw Object.assign(new Error(`Everything else saved. The carrier refused the caller ID: ${detail}`), { handled: true });
        }
      }
      return updated;
    },
    onSuccess: (updated) => onSaved?.(updated),
    onError: (e) => setError(e.handled ? e.message : (e.body?.error || e.message)),
  });

  const locations = Array.isArray(locsQ.data?.locations) ? locsQ.data.locations : [];

  // DOES THE PICKER CONTAIN THE ADDRESS THE LINE HOLDS?
  //
  // The options are only this customer's addresses, and a controlled <select> whose value
  // matches no option renders the FIRST option — "Not set". So a line pointing at a row that
  // is not among them read as having no 911 address at all, on a screen whose own banner was
  // at the same moment refusing a package change *because* that address is residential
  // (+13038235515, 2026-08-18). A line's 911 address is the one field on this form where
  // "not set" and "set to something this form can't see" must never look alike.
  //
  // `heldOffList` is also true while the query is in flight, which is what we want: the
  // option is rendered from the line itself, so the field shows the real address immediately
  // instead of flashing blank. Only once the list has ARRIVED can we say it isn't theirs.
  const heldOffList = Boolean(line.locationId)
    && !locations.some((l) => String(l.id) === String(line.locationId));
  const heldNotTheirs = heldOffList && locsQ.isSuccess;
  const heldLabel = line.locationLabel || line.locationAddress || `Location ${line.locationId}`;

  const tiers = Array.isArray(tiersQ.data) ? tiersQ.data : (tiersQ.data?.tiers || []);

  // SWITCHING A LINE BETWEEN RESIDENTIAL AND BUSINESS.
  //
  // The package cannot move on its own: `updateLine` compares the resulting PAIR, so a
  // residential→business change is a package AND an address in ONE save. That is already
  // what this form submits — both fields, one PATCH — but nothing said so, and picking the
  // package first produced a refusal that read like the package was disallowed. The clash is
  // now visible the moment it exists, along with the thing that resolves it: an address of
  // the new type. One street can carry one of each, which is how a business line lives at a
  // residential street address.
  const selTier = tiers.find((t) => String(t.id) === String(form.packageId)) || null;
  const selLocType = (() => {
    const hit = locations.find((l) => String(l.id) === String(form.locationId));
    if (hit) return hit.type || null;
    // The held-but-unlisted row: its type comes from the line, which is the same field the
    // server compares.
    if (form.locationId && String(form.locationId) === String(line.locationId)) {
      return line.locationType || null;
    }
    return null;
  })();
  const typeClash = Boolean(selTier?.type && selLocType && selTier.type !== selLocType);
  const addressesOfPkgType = typeClash ? locations.filter((l) => l.type === selTier.type) : [];

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        <div className="border-b border-gray-200 px-5 py-4">
          <h2 className="text-base font-semibold text-gray-900">
            {`Settings for ${line.number || `line ${line.id}`}`}
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            Recorded in Verso. Nothing here reaches the PBX or the device — that is what Apply
            does, and the page lists what is waiting.
            {cnamEditable && ' The caller ID is the exception: it goes to the carrier on Save.'}
          </p>
        </div>

        <div className="space-y-4 px-5 py-4">
          <div>
            <label htmlFor="ls-host" className="block text-xs font-medium uppercase tracking-wide text-gray-500">
              PBX host
            </label>
            <select
              id="ls-host"
              value={form.pbxAgentId}
              disabled={line.provisioned}
              title={line.provisioned
                ? 'Moving a live line between hosts is a migration, not a field change'
                : undefined}
              onChange={(e) => setForm((f) => ({ ...f, pbxAgentId: e.target.value }))}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-500"
            >
              <option value="">No host chosen</option>
              {Object.entries(pbxById).map(([agentId, name]) => (
                <option key={agentId} value={agentId}>{name}</option>
              ))}
            </select>
          </div>

          <div>
            {/* Route type moved to the Routing tab — it is a routing decision, and the
                ring group settings it governs live there (Kevin, 2026-08-10). */}
            <label htmlFor="ls-label" className="block text-xs font-medium uppercase tracking-wide text-gray-500">
              Name
            </label>
            <input
              id="ls-label"
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              placeholder={line.subscriberName || 'e.g. Front desk'}
              maxLength={15}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
            {form.label.trim().length >= 12 && (
              // Shown as it fills rather than only on rejection: a field that silently stops
              // accepting keystrokes at 15 reads as a broken keyboard.
              <p className="mt-1 text-xs text-gray-500">
                {form.label.trim().length}/15
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              What this line is called — shown on the phone, and to other extensions when it
              calls them. Up to 15 characters, which is what a phone screen holds. Left
              blank it falls back to the customer&rsquo;s name, so every line they own reads
              the same. Applied on the next build.
            </p>
          </div>

          {/* THE OTHER NAME. Distinct from the one above, and now visibly so: this is what
              strangers see, that is what the phone and the other extensions see. They were a
              gear apart on the same card, which is close enough to look like one setting shown
              twice and far enough that nobody checked both.

              This field submits to the carrier on Save — see the header. Everything else in
              this sheet is a Verso record waiting for a build. */}
          {cnamEditable && (
          <div className="border-t border-gray-100 pt-4">
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Caller ID
            </div>
            <p className="mt-1 text-xs text-gray-500">
              The name people see when this number calls them — not the same thing as the name
              above. {cnamOwn
                ? 'This number has its own name at the carrier, so saving renames it.'
                : `This number currently follows ${line.subscriberName || 'the customer'}. Saving gives it a `
                  + 'name of its own, leaving their other numbers as they are.'}
            </p>

            {cnamCalc.residential ? (
              <div className="mt-2 grid gap-3 sm:grid-cols-2">
                <div>
                  <label htmlFor="ls-cnam-first" className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                    First name
                  </label>
                  <input
                    id="ls-cnam-first"
                    value={cnam.first}
                    disabled={cnam.inherit}
                    onChange={(e) => setCnam((c) => ({ ...c, first: e.target.value }))}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-500"
                  />
                </div>
                <div>
                  <label htmlFor="ls-cnam-last" className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                    Last name
                  </label>
                  <input
                    id="ls-cnam-last"
                    value={cnam.last}
                    disabled={cnam.inherit}
                    onChange={(e) => setCnam((c) => ({ ...c, last: e.target.value }))}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-500"
                  />
                </div>
              </div>
            ) : (
              <div className="mt-2">
                <label htmlFor="ls-cnam-name" className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                  {cnamType === 'business' ? 'Business name' : 'Name to present'}
                </label>
                <input
                  id="ls-cnam-name"
                  value={cnam.typed}
                  disabled={cnam.inherit}
                  onChange={(e) => setCnam((c) => ({ ...c, typed: e.target.value }))}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
            )}

            <p className="mt-1 text-xs text-gray-500">
              {cnamShapeHint(cnamType)}
              {' '}
              <span className={cnamCalc.tooLong ? 'font-medium text-red-700' : undefined}>
                {cnamCalc.remaining} of {CNAM_MAX} characters left
              </span>
              {' — carriers cut off the rest.'}
            </p>

            {cnamOwn && (
              // The way back off an override. Without it, a number given a name of its own can
              // never follow the customer's again.
              <label className="mt-2 flex items-start gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={cnam.inherit}
                  onChange={(e) => setCnam((c) => ({ ...c, inherit: e.target.checked }))}
                  className="mt-0.5"
                />
                <span>
                  Follow {line.subscriberName || 'the customer'}&rsquo;s name instead
                  <span className="block text-xs text-gray-500">
                    Drops this number&rsquo;s own carrier profile.
                  </span>
                </span>
              </label>
            )}
          </div>
          )}

          {/* Two across: the package and the address the carrier holds for 911. They are
              side by side because their types have to agree, and the clash below reads as one
              decision rather than two fields that happen to disagree. */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="ls-package" className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                Package
              </label>
              <select
                id="ls-package"
                value={form.packageId}
                onChange={(e) => setForm((f) => ({ ...f, packageId: e.target.value }))}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">No package</option>
                {tiers.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}{t.type ? ` · ${t.type}` : ''}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="ls-location" className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                911 address
              </label>
              <select
                id="ls-location"
                value={form.locationId}
                onChange={(e) => setForm((f) => ({ ...f, locationId: e.target.value }))}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                {/* Offered ONLY when the line genuinely has no address. On a line that has
                    one this is a control that cannot do what it says — the API has no way to
                    clear the field — so it is not shown at all. */}
                {!line.locationId && <option value="">Not set</option>}
                {heldOffList && (
                  <option value={String(line.locationId)}>
                    {heldLabel}{line.locationType ? ` · ${line.locationType}` : ''}
                    {heldNotTheirs ? ' — not one of this customer’s addresses' : ''}
                  </option>
                )}
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.label || l.address_line1 || l.addressLine1}{l.type ? ` · ${l.type}` : ''}
                  </option>
                ))}
              </select>
              {heldNotTheirs && (
                <p className="mt-1 text-xs text-amber-700">
                  This line’s 911 address is not one of this customer’s addresses, so the server
                  would refuse it if this form sent it back. Assign that address to this
                  customer, or choose one of theirs.
                </p>
              )}
            </div>
          </div>
          {/* The two types have to agree, and this org has one address of each type at the
              same street — so the type is part of the choice, not decoration. */}
          <p className="text-xs text-gray-500">
            The address type has to match the package: the carrier requires the 911 record and
            the caller-ID type to agree.
          </p>

          {typeClash && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <p>
              <strong>{selTier.name}</strong> is {selTier.type} service and the address chosen
              above is {selLocType}. The package cannot change on its own — set both here, in
              one save.
            </p>
            <p className="mt-1">
              {addressesOfPkgType.length > 0
                ? `Choose one of this customer’s ${selTier.type} addresses above: `
                  + `${addressesOfPkgType.map((l) => l.label || l.address_line1 || l.addressLine1).join(', ')}.`
                : `This customer has no ${selTier.type} address on file. Add one on their `
                  + `Locations tab — one street can carry an address of each type — then choose it here.`}
            </p>
          </div>
          )}

          <div>
            <label htmlFor="ls-status" className="block text-xs font-medium uppercase tracking-wide text-gray-500">
              Status
            </label>
            <select
              id="ls-status"
              value={form.status}
              onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {line.sipAccountId && (
            <div className="border-t border-gray-100 pt-4">
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Voicemail</div>
              <p className="mt-1 text-sm text-gray-700">
                {line.voicemailEnabled !== false ? 'On for this line.' : 'Off for this line.'}
                {' '}
                <span className="text-xs text-gray-500">
                  Switching it off can delete the mailbox and the messages in it, so it is not
                  changed from here.
                </span>
              </p>
              <label className="mt-2 flex items-start gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.resetPin}
                  disabled={line.voicemailEnabled === false}
                  onChange={(e) => setForm((f) => ({ ...f, resetPin: e.target.checked }))}
                  className="mt-0.5"
                />
                <span>
                  Reset the PIN to the extension
                  <span className="block text-xs text-gray-500">
                    Sends the subscriber back through voicemail setup. Reaches the PBX on the
                    next Apply.
                  </span>
                </span>
              </label>
            </div>
          )}

          {error && <p className="text-sm text-red-700">{error}</p>}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!dirty || save.isPending}
            onClick={() => { setError(null); save.mutate(); }}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
